import { useEffect, useMemo, useRef, useState } from 'react';
import { Grip, X } from 'lucide-react';
import './GameWindow.css';

const EDGE_MARGIN = 8;
export const GAME_WINDOW_STORAGE_PREFIX = 'dopeman.ui.v2.gamewindow.geometry.v1.';
export const GAME_WINDOW_RESET_EVENT = 'dopeman-v2-reset-window-layout';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function mobileWindowQuery() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 760px), (pointer: coarse)').matches;
}

function initialRect(width, height) {
  if (typeof window === 'undefined') return { x: EDGE_MARGIN, y: EDGE_MARGIN, width, height };
  const safeWidth = Math.min(width, Math.max(1, window.innerWidth - EDGE_MARGIN * 2));
  const safeHeight = Math.min(height, Math.max(1, window.innerHeight - EDGE_MARGIN * 2));
  return {
    x: Math.max(EDGE_MARGIN, Math.round((window.innerWidth - safeWidth) / 2)),
    y: Math.max(EDGE_MARGIN, Math.round((window.innerHeight - safeHeight) / 2)),
    width: safeWidth,
    height: safeHeight,
  };
}

function clampRect(rect, minWidth, minHeight) {
  if (typeof window === 'undefined') return rect;
  const maxWidth = Math.max(1, window.innerWidth - EDGE_MARGIN * 2);
  const maxHeight = Math.max(1, window.innerHeight - EDGE_MARGIN * 2);
  const effectiveMinWidth = Math.min(minWidth, maxWidth);
  const effectiveMinHeight = Math.min(minHeight, maxHeight);
  const width = clamp(Number(rect?.width || 0), effectiveMinWidth, maxWidth);
  const height = clamp(Number(rect?.height || 0), effectiveMinHeight, maxHeight);
  return {
    width,
    height,
    x: clamp(Number(rect?.x || EDGE_MARGIN), EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerWidth - width - EDGE_MARGIN)),
    y: clamp(Number(rect?.y || EDGE_MARGIN), EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN)),
  };
}

function geometryKey(id) {
  return `${GAME_WINDOW_STORAGE_PREFIX}${String(id || 'window')}`;
}

function loadGeometry(id, fallback, minWidth, minHeight) {
  if (typeof window === 'undefined' || !id) return clampRect(fallback, minWidth, minHeight);
  try {
    const parsed = JSON.parse(window.localStorage.getItem(geometryKey(id)) || 'null');
    if (!parsed || !['x', 'y', 'width', 'height'].every((key) => Number.isFinite(Number(parsed[key])))) return clampRect(fallback, minWidth, minHeight);
    return clampRect(parsed, minWidth, minHeight);
  } catch {
    return clampRect(fallback, minWidth, minHeight);
  }
}


export function resetGameWindowGeometry(id = '') {
  if (typeof window === 'undefined') return;
  try {
    if (id) window.localStorage.removeItem(geometryKey(id));
    else {
      for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
        const key = window.localStorage.key(index);
        if (key?.startsWith(GAME_WINDOW_STORAGE_PREFIX)) window.localStorage.removeItem(key);
      }
    }
  } catch {
    // Layout reset is presentation-only; storage failures must never block gameplay.
  }
  window.dispatchEvent(new CustomEvent(GAME_WINDOW_RESET_EVENT, { detail: { id } }));
}

function saveGeometry(id, rect) {
  if (typeof window === 'undefined' || !id) return;
  try {
    window.localStorage.setItem(geometryKey(id), JSON.stringify({ x: Math.round(rect.x), y: Math.round(rect.y), width: Math.round(rect.width), height: Math.round(rect.height) }));
  } catch {
    // Geometry persistence is a convenience only; storage failures must never block gameplay.
  }
}

export function GameWindow({
  id,
  title,
  subtitle,
  children,
  onClose,
  onFocus,
  zIndex = 9000,
  isFocused = true,
  initialWidth = 760,
  initialHeight = 620,
  minWidth = 430,
  minHeight = 340,
  className = '',
}) {
  const [isMobile, setIsMobile] = useState(mobileWindowQuery);
  const minSize = useMemo(() => ({ width: minWidth, height: minHeight }), [minWidth, minHeight]);
  const [rect, setRect] = useState(() => loadGeometry(id, initialRect(initialWidth, initialHeight), minWidth, minHeight));
  const [interaction, setInteraction] = useState(null);
  const rectRef = useRef(rect);
  const windowRef = useRef(null);
  rectRef.current = rect;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 760px), (pointer: coarse)');
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (isMobile) return undefined;
    setRect(loadGeometry(id, initialRect(initialWidth, initialHeight), minSize.width, minSize.height));
    return undefined;
  }, [id, initialWidth, initialHeight, isMobile, minSize]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const keepInsideViewport = () => setRect((current) => clampRect(current, minSize.width, minSize.height));
    window.addEventListener('resize', keepInsideViewport);
    return () => window.removeEventListener('resize', keepInsideViewport);
  }, [minSize]);

  useEffect(() => {
    if (isMobile || interaction) return undefined;
    const timer = window.setTimeout(() => saveGeometry(id, rect), 120);
    return () => window.clearTimeout(timer);
  }, [id, interaction, isMobile, rect]);

  useEffect(() => {
    if (!interaction || isMobile || typeof window === 'undefined') return undefined;
    const move = (event) => {
      event.preventDefault();
      const dx = event.clientX - interaction.startX;
      const dy = event.clientY - interaction.startY;
      const start = interaction.rect;
      let next = { ...start };
      if (interaction.kind === 'move') {
        next.x = start.x + dx;
        next.y = start.y + dy;
      } else {
        const edge = interaction.edge;
        if (edge.includes('e')) next.width = start.width + dx;
        if (edge.includes('s')) next.height = start.height + dy;
        if (edge.includes('w')) { next.x = start.x + dx; next.width = start.width - dx; }
        if (edge.includes('n')) { next.y = start.y + dy; next.height = start.height - dy; }
      }
      setRect(clampRect(next, minSize.width, minSize.height));
    };
    const end = () => {
      saveGeometry(id, rectRef.current);
      setInteraction(null);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', end, { once: true });
    window.addEventListener('pointercancel', end, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [id, interaction, isMobile, minSize]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reset = (event) => {
      const targetId = String(event.detail?.id || '');
      if (targetId && targetId !== String(id || '')) return;
      const fallback = clampRect(initialRect(initialWidth, initialHeight), minSize.width, minSize.height);
      setRect(fallback);
      rectRef.current = fallback;
    };
    window.addEventListener(GAME_WINDOW_RESET_EVENT, reset);
    return () => window.removeEventListener(GAME_WINDOW_RESET_EVENT, reset);
  }, [id, initialHeight, initialWidth, minSize]);

  useEffect(() => {
    if (!isFocused) return undefined;
    windowRef.current?.focus?.({ preventScroll: true });
    return undefined;
  }, [isFocused]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const keyDown = (event) => {
      if (event.key === 'Escape' && isFocused) onClose?.();
    };
    window.addEventListener('keydown', keyDown);
    return () => window.removeEventListener('keydown', keyDown);
  }, [isFocused, onClose]);

  const beginMove = (event) => {
    if (isMobile || event.button !== 0 || event.target.closest('button')) return;
    onFocus?.(id);
    setInteraction({ kind: 'move', startX: event.clientX, startY: event.clientY, rect });
  };

  const beginResize = (edge) => (event) => {
    if (isMobile || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    onFocus?.(id);
    setInteraction({ kind: 'resize', edge, startX: event.clientX, startY: event.clientY, rect });
  };

  const style = isMobile ? { zIndex } : {
    zIndex,
    left: `${rect.x}px`,
    top: `${rect.y}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };

  return <section
    ref={windowRef}
    className={`v2-game-window ${isMobile ? 'mobile' : 'desktop'} ${isFocused ? 'focused' : 'background'} ${interaction ? 'interacting' : ''} ${className}`}
    style={style}
    role="dialog"
    aria-modal="false"
    aria-label={title}
    data-window-id={id}
    tabIndex={-1}
    onPointerDown={() => onFocus?.(id)}
  >
    <header className="v2-window-titlebar" onPointerDown={beginMove}>
      <Grip aria-hidden="true" />
      <div><strong>{title}</strong>{subtitle && <small>{subtitle}</small>}</div>
      <button type="button" onClick={onClose} aria-label={`Close ${title}`} title="Close"><X /></button>
    </header>
    <div className="v2-window-body">{children}</div>
    {!isMobile && ['n','e','s','w','ne','nw','se','sw'].map((edge) => <i key={edge} className={`v2-window-resize resize-${edge}`} onPointerDown={beginResize(edge)} aria-hidden="true" />)}
  </section>;
}
